import moment from 'moment';
import { delimiters } from '../../shared/constants';
import { select } from '../../shared/utils/dom';
import { createError, removeError } from '../../shared/formErrors';
import { regex, isPasswordValid } from '../../shared/constants/validation';
import multistepMixin from '../../shared/vue/mixins/multistep';
import { oneTimePaymentApi } from '../api';
import dataLayerPush from '../../shared/utils/analytics';

const el = select('[data-vue-app="guest-pay"]');
if (el) {
  new Vue({ // eslint-disable-line no-new
    el,
    delimiters,
    mixins: [multistepMixin],
    data() {
      return {
        // the account number
        accountNumber: '',
        // agree to terms and conditions
        agree: false,
        isReady: false,
        // billing info
        billing: {
          // bank account number
          bankAccountNumber: '',
          // bank account type
          bankAccountType: 'Checking',
          // bank routing number
          bankRouting: '',
          // email
          email: '',
          // first name
          firstName: '',
          // payment amount
          paymentAmount: '',
          // last name
          lastName: '',
        },
        modelLdc: '',
        // password
        modelGuestPassword: null,
        // confirm password
        modelGuestConfirmPassword: null,
        // password valid
        modelGuestPasswordValid: false,
        // confirm password matches
        modelConfirmGuestPasswordMatch: false,
        // live text
        liveText: '',
        // payment date should be current date (formatted)
        paymentDate: moment().format('LL'),
        errorMessage: null,
      };
    },
    computed: {
      accountNumberMask() {
        if (this.modelLdc) {
          if (this.modelLdc === 'NI' || this.modelLdc === 'BS') {
            return '###-###-###-#';
          }
          return '########-###-####';
        }
        return null;
      },
      accountNumberPlaceholder() {
        if (this.modelLdc) {
          // The placeholder for the account number changes based on LDC.
          if (this.modelLdc === 'NI' || this.modelLdc === 'BS') {
            return 'XXX - XXX - XXX - X';
          }
          return 'XXXXXXXX - XXX - XXXX';
        }
        return null;
      },
      // disabled button state for account number lookup button
      enableLookupAccountNumber() {
        // TODO: Better validation for keyed in account number.
        return this.accountNumber !== '';
      },
      /**
       * Formatted Pay From information for the receipt page.
       * @returns {string}
       */
      formattedPayFromSummary() {
        // Create a formatted string "Routing XXX 1234 | Checking XXXX 1234"
        return `Routing XXX ${this.billing.bankRouting.slice(-4)} | ${this.billing.bankAccountType} XXXX ${this.billing.bankAccountNumber.slice(-4)}`;
      },
    },
    watch: {
      step(newVal, oldVal) { // eslint-disable-line no-unused-vars
        /**
         * Changes live text depending on the form step
         */
        if (this.step === 2) {
          this.liveText = 'Moving to Step 2 to enter your payment information';
        }
      },
    },
    methods: {
      /**
       * Keyup on password field
       */
      keyUpPassword(event) {
        // password validity check
        if (event.target === this.$refs.guestPassword) {
          if (this.modelGuestPassword && this.modelGuestPassword.length < 8) {
            this.modelGuestPasswordValid = false;
          } else if (!isPasswordValid(this.$refs.guestPassword.value)) {
            this.modelGuestPasswordValid = false;
          } else {
            this.modelGuestPasswordValid = true;
          }
        }

        // confirm password validity check
        if (event.target === this.$refs.confirmGuestPassword) {
          // check they are the same thing
          if (this.modelGuestConfirmPassword && this.modelGuestPassword && this.modelGuestPasswordValid && this.modelGuestConfirmPassword === this.modelGuestPassword) {
            this.modelConfirmGuestPasswordMatch = true
            removeError(this.$refs.guestPassword, this.$refs.guestPasswordError);
          } else {
            this.modelConfirmGuestPasswordMatch = false
            removeError(this.$refs.confirmGuestPassword, this.$refs.confirmGuestPasswordError);
          }
        }
      },
      /**
       * Form submit handler
       */
      submitRegistration(event) {
        event.preventDefault();
        let hasError = false;

        // validate password fields
        if (!this.modelGuestPassword) {
          createError(this.$refs.guestPassword, this.$refs.guestPasswordError, 'Please provide a password.');
          hasError = true
        } else if (!isPasswordValid(this.$refs.guestPassword.value)) {
          createError(this.$refs.guestPassword, this.$refs.guestPasswordError, 'The password you provided does not meet these password requirements.');
          hasError = true
        } else {
          removeError(this.$refs.guestPassword, this.$refs.guestPasswordError);
        }
        // validate confirm password field
        if (!this.modelGuestConfirmPassword) {
          createError(this.$refs.confirmGuestPassword, this.$refs.confirmGuestPasswordError, 'Please confirm password');
          hasError = true
        } else if (this.modelGuestPassword !== this.modelGuestConfirmPassword) {
          createError(this.$refs.confirmGuestPassword, this.$refs.confirmGuestPasswordError, 'The passwords you provided do not match.');
          hasError = true
        } else {
          removeError(this.$refs.confirmGuestPassword, this.$refs.confirmGuestPasswordError);
        }

        if (hasError) {
          // update live text to let the user know there are errors
          this.liveText = 'The form has errors. Please make sure your information is correct.';
        }
      },
      searchByAccount() {
        this.loading = true;
        oneTimePaymentApi.getGuestPayEligibility(this.accountNumber)
          .then((response) => {
            if (response === null) {
              response = {
                eligible: false,
                ineligibleReason: "Account not found",
              };
            } else if (response.eligible) {
              this.errorMessage = null;
              this.nextStep().then(() => {
              })
            } else {
              this.errorMessage = response.ineligibleReason;
            }
            this.loading = false;
          })
          .catch(() => {
            this.scrollUp();
            this.loading = false;
          });
      },
      /**
       * Handle enter clicked while focused in account number field.
       */
      handleEnter() {
        if (!this.disabledAccountNumber) {
          this.clickSearchForAccount();
        }
      },
      /**
       * Helper function to validate the form.
       */
      validateForm() {
        let validForm = true;
        if (this.billing.lastName === '') {
          createError(this.$refs.lastName, this.$refs.lastNameError, 'Please provide a last name or company.');
          validForm = false;
        } else {
          removeError(this.$refs.lastName, this.$refs.lastNameError);
        }
        if (this.billing.firstName === '') {
          createError(this.$refs.firstName, this.$refs.firstNameError, 'Please provide a first name.');
          validForm = false;
        } else {
          removeError(this.$refs.firstName, this.$refs.firstNameError);
        }
        if (this.billing.bankRouting === '') {
          createError(this.$refs.bankRouting, this.$refs.bankRoutingError, 'Please enter a routing number.');
          validForm = false;
        } else {
          removeError(this.$refs.bankRouting, this.$refs.bankRoutingError);
        }
        if (this.billing.bankAccountNumber === '') {
          createError(this.$refs.bankAccount, this.$refs.bankAccountError, 'Please enter an account number.');
          validForm = false;
        } else {
          removeError(this.$refs.bankAccount, this.$refs.bankAccountError);
        }
        if (this.billing.paymentAmount === '') {
          createError(this.$refs.amount, this.$refs.amountError, 'Please enter an amount greater than 0.00.');
          validForm = false;
        } else {
          removeError(this.$refs.amount, this.$refs.amountError);
        }
        if (!regex.email.test(this.billing.email)) {
          createError(this.$refs.email, this.$refs.emailError, 'Please enter a valid email.');
          validForm = false;
        } else {
          removeError(this.$refs.email, this.$refs.emailError);
        }
        return validForm;
      },
      /**
       * Submit form.
       */
      submitPayment() {
        if (this.validateForm()) {
          this.loading = true;
          const guestPayParams = {
            emailAddress: this.billing.email,
            firstName: this.billing.firstName,
            lastName: this.billing.lastName,
            paymentRequest: {
              customerAccountId: this.accountNumber,
              paymentAmount: this.billing.paymentAmount,
              // paymentDateTime: moment(data.paymentDate, 'MMM D, YYYY').format("YYYY-MM-DD[T]HH:mm:ss[Z]"),
              autopayUseBillDueDate: false,
              checkPaymentCreateRequest: {
                accountNumber: this.billing.bankAccountNumber,
                routingNumber: this.billing.bankRouting,
                accountType: this.billing.bankAccountType,
              },
            },
          };
          oneTimePaymentApi.makeGuestPayment(this.accountNumber, guestPayParams)
            .then((response) => {
              if (response.data != null) {
                this.errorMessage = null;
                dataLayerPush('GuestPayment');
                this.setStep(4).then(() => {
                  this.loading = false;
                });
              } else {
                this.errorMessage = "Something went wrong."
                this.scrollUp();
                this.loading = false;
              }
            })
            .catch(() => {
              this.errorMessage = "Something went wrong."
              this.scrollUp();
              this.loading = false;
            })
        }
      },
    },
    /**
     * Mounted
     */
    mounted() {
      // Find the LDC to format account number
      if (this.$el.dataset.ldc) {
        this.modelLdc = this.$el.dataset.ldc;
        // now we're ready to mask
        this.isReady = true;
      }
    },
  });
}



// WEBPACK FOOTER //
// ./src/assets/toolkit/scripts/portal/vue/guest-pay.js